import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';
import { validateDescription } from '@/utils/lineItemDisplayHelpers';

interface UpdateDescriptionParams {
  lineItemId: string;
  newDescription: string;
  estimateId: string;
}

interface UpdateUnitCostParams {
  lineItemId: string;
  newUnitCost: number;
  quantity: number;
  estimateId: string;
}

interface ResetPricingParams {
  lineItemId: string;
  estimateId: string;
}

/**
 * Hook for managing manual overrides on estimate line items
 * Handles description and pricing updates with validation
 */
export const useLineItemManualOverride = () => {
  const queryClient = useQueryClient();

  // Update manual description
  const updateManualDescription = useMutation({
    mutationFn: async ({ lineItemId, newDescription, estimateId }: UpdateDescriptionParams) => {
      // Validate description
      const validation = validateDescription(newDescription);
      if (!validation.valid) {
        throw new Error(validation.error || 'Invalid description');
      }

      // Show warning if present
      if (validation.warning) {
        toast.warning(validation.warning);
      }

      const { data, error } = await supabase
        .from('estimate_line_items')
        .update({
          manual_description: newDescription.trim()
        })
        .eq('id', lineItemId)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: (_, variables) => {
      toast.success('Description updated');
      queryClient.invalidateQueries({ queryKey: ['estimateLineItems', variables.estimateId] });
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to update description');
    }
  });

  // Update manual unit cost (also calculates and stores total)
  const updateManualUnitCost = useMutation({
    mutationFn: async ({ lineItemId, newUnitCost, quantity, estimateId }: UpdateUnitCostParams) => {
      if (newUnitCost < 0) {
        throw new Error('Unit cost cannot be negative');
      }
      // Clamp to 0 minimum for safety
      const safeCost = Math.max(0, newUnitCost);

      const newTotalCost = safeCost * quantity;

      const { data, error } = await supabase
        .from('estimate_line_items')
        .update({
          manual_override: true,
          manual_unit_cost: safeCost,
          manual_total_cost: newTotalCost
        })
        .eq('id', lineItemId)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: (_, variables) => {
      toast.success('Price updated');
      queryClient.invalidateQueries({ queryKey: ['estimateLineItems', variables.estimateId] });
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to update price');
    }
  });

  // Reset pricing (clear manual overrides for price only, not description)
  const resetPricing = useMutation({
    mutationFn: async ({ lineItemId, estimateId }: ResetPricingParams) => {
      const { data, error } = await supabase
        .from('estimate_line_items')
        .update({
          manual_override: false,
          manual_total_cost: null,
          manual_unit_cost: null
        })
        .eq('id', lineItemId)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: (_, variables) => {
      toast.success('Pricing reset to default');
      queryClient.invalidateQueries({ queryKey: ['estimateLineItems', variables.estimateId] });
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to reset pricing');
    }
  });

  return {
    updateManualDescription,
    updateManualUnitCost,
    resetPricing
  };
};
